import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import '../ghs-hero-banner-light/ghs-hero-banner-light';
import '../ghs-hero-banner/ghs-hero-banner';

import { BackgroundVariant, HorizontalAlignmentVariant } from '../types';

/**
 * Switching hero banner component. Meant for A/B design testing Locations pages
 * @element ghs-hero-banner-wrapper
 * @slot - default
 * @slot hero-banner-buttons - for buttons
 * @slot hero-banner-breadcrumb - for breadcrumb
 * @slot hero-banner-hero-image - for hero image
 * @slot hero-banner-eyebrow - for eyebrow text
 * @slot hero-banner-title - for main title (required)
 * @slot hero-banner-body-text - for main page description
 * @slot hero-banner-buttons - for CTA buttons
 * @slot hero-banner-phone-number - for location phone number
 */
@customElement('ghs-hero-banner-wrapper')
export class GhsHeroBannerWrapper extends OutlineElement {
  /**
   * Changes the background of the component
   * @param backgroundVariant
   * @default 'blue-gradient'
   */
  @property({ type: String, attribute: 'background-variant', reflect: true })
  backgroundVariant: BackgroundVariant = 'green-gradient';

  @property({ type: Boolean, attribute: 'alternate', reflect: true })
  alternate = false;

  /**
   * Hides the image on mobile if true
   */
  @property({ type: Boolean, attribute: 'hide-mobile-image', reflect: true })
  hideMobileImage = false;

  /**
   * The Banner content's alignment relative to the image.
   * @param alignment
   * @default 'left-align'
   */
  @property({ type: String, reflect: true })
  alignment: HorizontalAlignmentVariant = 'right-align';

  /**
   * Storing references to the slot elements.
   * We need to clone them to pass into the Shadow DOM hero component.
   * Otherwise we can't restore the original Light DOM implementation.
   */
  @state() breadcrumbSlot: Node | null;
  @state() eyebrowSlot: Node | null;
  @state() titleSlot: Node | null;
  @state() bodyTextSlot: Node | null;
  @state() buttonsSlot: Node | null;
  @state() phoneSlot: Node | null;
  @state() imageSlot: Node | null;
  @state() CTAButtonSlot: Node | null;

  connectedCallback(): void {
    super.connectedCallback();
    this.breadcrumbSlot = this.querySelector('[slot="hero-banner-breadcrumb"]');
    this.eyebrowSlot = this.querySelector('[slot="hero-banner-eyebrow"]');
    this.titleSlot = this.querySelector('[slot="hero-banner-title"]');
    this.bodyTextSlot = this.querySelector('[slot="hero-banner-body-text"]');
    this.buttonsSlot = this.querySelector('[slot="hero-banner-buttons"]');
    this.phoneSlot = this.querySelector('[slot="hero-banner-phone-number"]');
    this.imageSlot = this.querySelector('[slot="hero-banner-hero-image"]');
    this.CTAButtonSlot = this.querySelector('[slot="hero-banner-buttons"]');
  }

  getBreadCrumbSlot(): Node {
    if (this.alternate) {
      var breadcrumbs = this.breadcrumbSlot?.cloneNode(true) as Element;
      (breadcrumbs?.querySelector('ghs-breadcrumbs') as Element).setAttribute(
        'style-variant',
        'background-light'
      );
      return breadcrumbs;
    }
    return this.breadcrumbSlot as Node;
  }

  getImageSlot(): Node {
    if (this.alternate) {
      var image = this.imageSlot?.cloneNode(true) as Element;
      image?.setAttribute('image-ratio', '16/9');
      return image;
    }
    return this.imageSlot as Node;
  }

  render(): TemplateResult {
    // Construct the slots to pass in to the child hero banner.
    const slots = html`
      ${this.breadcrumbSlot !== null
        ? html`${this.getBreadCrumbSlot()}`
        : nothing}
      ${this.imageSlot !== null ? html`${this.getImageSlot()}` : nothing}
      ${this.eyebrowSlot !== null
        ? html`${this.alternate
            ? this.eyebrowSlot?.cloneNode(true)
            : html`<slot name="hero-banner-eyebrow" />`}`
        : nothing}
      ${this.alternate ? this.titleSlot?.cloneNode(true) : this.titleSlot}
      ${this.bodyTextSlot !== null
        ? html`${this.alternate
            ? this.bodyTextSlot?.cloneNode(true)
            : this.bodyTextSlot}`
        : nothing}
      ${this.CTAButtonSlot !== null
        ? html`${this.alternate
            ? this.CTAButtonSlot?.cloneNode(true)
            : this.CTAButtonSlot}`
        : nothing}
      ${this.phoneSlot !== null && !this.alternate
        ? html`<div slot="hero-banner-phone-number">
            <slot name="hero-banner-phone-number" />
          </div>`
        : nothing}
    `;

    return html`${this.alternate
      ? html`<ghs-hero-banner
          alignment="left-align"
          alternate=${this.alternate}
          background-variant="${this.backgroundVariant}"
          ${this.hideMobileImage ? html`hide-mobile-image` : nothing}
          >${slots}</ghs-hero-banner
        >`
      : html`<ghs-hero-banner-light
          background-variant="${this.backgroundVariant}"
          ${this.hideMobileImage ? html`hide-mobile-image` : nothing}
          >${slots}</ghs-hero-banner-light
        >`}`;
  }
}
